import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import styles from "../modals/PrintableInvoice.module.css";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Layout from "../../component/layout";
import InvoicePDF from "../pdf/InvoicePDF";
import { AddPaymentModal, PaymentsListModal } from "../modals";

const billingSettingsURL = "/api/core/billing/settings";

function InvoicePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoice, facility } = location.state || {};
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showPaymentsListModal, setShowPaymentsListModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(invoice);
  const [headerSettings, setHeaderSettings] = useState({
    companyName: "",
    companyAddress: "",
    companyPhone: "",
    companyEmail: "",
    companyWebsite: "",
    taxNumber: "",
    logoPreview: null,
  });
  const [footerSettings, setFooterSettings] = useState({
    bankName: "",
    accountName: "",
    accountNumber: "",
    swiftCode: "",
    paymentTerms: "Net 30 days",
    latePaymentFee: "",
    taxRate: "",
    additionalNotes: "",
    supportEmail: "",
    supportPhone: "",
    termsAndConditions: "",
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Use invoice header data if available, otherwise fall back to settings
  const getDisplayHeaderSettings = () => {
    if (invoice?.header) {
      return {
        companyName:
          invoice.header.companyName || headerSettings.companyName || "",
        companyAddress:
          invoice.header.companyAddress || headerSettings.companyAddress || "",
        companyPhone:
          invoice.header.companyPhone || headerSettings.companyPhone || "",
        companyEmail:
          invoice.header.companyEmail || headerSettings.companyEmail || "",
        companyWebsite:
          invoice.header.companyWebsite || headerSettings.companyWebsite || "",
        taxNumber: invoice.header.taxNumber || headerSettings.taxNumber || "",
        logoPreview: invoice.header.logo || headerSettings.logoPreview || null,
      };
    }
    return headerSettings;
  };

  // Use invoice footer data if available, otherwise fall back to settings
  const getDisplayFooterSettings = () => {
    if (invoice?.footer) {
      return {
        bankName: invoice.footer.bankName || footerSettings.bankName || "",
        accountName:
          invoice.footer.accountName || footerSettings.accountName || "",
        accountNumber:
          invoice.footer.accountNumber || footerSettings.accountNumber || "",
        swiftCode: invoice.footer.swiftCode || footerSettings.swiftCode || "",
        paymentTerms:
          invoice.footer.paymentTerms ||
          footerSettings.paymentTerms ||
          "Net 30 days",
        latePaymentFee:
          invoice.footer.latePaymentFee || footerSettings.latePaymentFee || "",
        taxRate:
          invoice.footer.taxRate ||
          footerSettings.taxRate ||
          invoice.taxRate ||
          "",
        additionalNotes:
          invoice.footer.additionalNotes ||
          footerSettings.additionalNotes ||
          "",
        supportEmail:
          invoice.footer.supportEmail || footerSettings.supportEmail || "",
        supportPhone:
          invoice.footer.supportPhone || footerSettings.supportPhone || "",
        termsAndConditions:
          invoice.footer.termsAndConditions ||
          footerSettings.termsAndConditions ||
          "",
      };
    }
    return footerSettings;
  };

  // Get the display settings
  const displayHeaderSettings = getDisplayHeaderSettings();
  const displayFooterSettings = getDisplayFooterSettings();

  useEffect(() => {
    if (!settingsLoaded) {
      loadBillingSettings();
    }
  }, [settingsLoaded]);

  const loadBillingSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await makeRequest2(billingSettingsURL, "GET", {});

      if (response.success && response.data) {
        const actualData = response.data.data || response.data;
        const headerData = actualData.header || {};
        const footerData = actualData.footer || {};

        setHeaderSettings({
          companyName: headerData.companyName || "",
          companyAddress: headerData.companyAddress || "",
          companyPhone: headerData.companyPhone || "",
          companyEmail: headerData.companyEmail || "",
          companyWebsite: headerData.companyWebsite || "",
          taxNumber: headerData.taxNumber || "",
          logoPreview: headerData.logo || null,
        });

        setFooterSettings({
          bankName: footerData.bankName || "",
          accountName: footerData.accountName || "",
          accountNumber: footerData.accountNumber || "",
          swiftCode: footerData.swiftCode || "",
          paymentTerms: footerData.paymentTerms || "Net 30 days",
          latePaymentFee: footerData.latePaymentFee || "",
          taxRate: footerData.taxRate || "",
          additionalNotes: footerData.additionalNotes || "",
          supportEmail: footerData.supportEmail || "",
          supportPhone: footerData.supportPhone || "",
          termsAndConditions: footerData.termsAndConditions || "",
        });

        setSettingsLoaded(true);
      } else {
        throw new Error(response.error || "Failed to load billing settings");
      }
    } catch (error) {
      console.error("Error loading billing settings:", error);
      const savedHeaderSettings = localStorage.getItem("billingHeaderSettings");
      const savedFooterSettings = localStorage.getItem("billingFooterSettings");

      if (savedHeaderSettings) {
        setHeaderSettings(JSON.parse(savedHeaderSettings));
      }
      if (savedFooterSettings) {
        setFooterSettings(JSON.parse(savedFooterSettings));
      }

      toastify(
        "Using cached billing settings. Please check your settings configuration.",
        "warn",
      );
    } finally {
      setLoadingSettings(false);
    }
  };

  // Print function using React-PDF
  const handlePrint = async () => {
    if (!displayHeaderSettings.companyName) {
      toastify(
        "Billing settings not configured. Please configure company information first.",
        "warn",
      );
      return;
    }

    try {
      toastify("Preparing invoice for printing...", "info");

      const pdfBlob = await pdf(
        <InvoicePDF
          invoice={invoice}
          facility={facility}
          displayHeaderSettings={displayHeaderSettings}
          displayFooterSettings={displayFooterSettings}
        />,
      ).toBlob();

      const url = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(url, "_blank");

      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };

        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 5000);
      } else {
        toastify(
          "Popup blocked. Please allow popups or download PDF instead.",
          "warn",
        );
      }

      toastify("Invoice prepared for printing!", "success");
    } catch (error) {
      console.error("Error printing invoice:", error);
      toastify(`Error printing invoice: ${error.message}`, "error");
    }
  };

  const handleSendInvoice = () => {
    if (!displayHeaderSettings.companyName) {
      toastify(
        "Billing settings not configured. Please configure company information first.",
        "warn",
      );
      return;
    }
    toastify("Email functionality coming soon!", "info");
  };

  // Get filename for PDF
  const getPDFFilename = () => {
    const invoiceNumber = invoice?.invoiceNumber || "INV-0000";
    const facilityName = invoice?.facilityName || facility?.name || "Invoice";
    const date = new Date().toISOString().split("T")[0];
    return `${invoiceNumber}_${facilityName.replace(/[^a-zA-Z0-9]/g, "_")}_${date}.pdf`;
  };

  // Helper functions
  const calculateSubtotal = () => {
    if (invoice?.items) {
      return invoice.items.reduce((sum, item) => sum + (item.amount || 0), 0);
    }
    return invoice?.amount || invoice?.totalAmount || 0;
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    const taxRate = parseFloat(
      displayFooterSettings?.taxRate || invoice?.taxRate || 0,
    );
    if (taxRate <= 0) return 0;
    return (subtotal * taxRate) / 100;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax();
    return subtotal + tax;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    return `KES ${amount?.toLocaleString() || "0"}`;
  };

  const handlePaymentAdded = (response) => {
    console.log("Payment added:", response);
    toastify("Payment recorded successfully!", "success");
    // Update invoice data with new payment information
    if (response.invoice) {
      setInvoiceData({ ...invoiceData, status: response.invoice.status });
    }
  };

  const handlePaymentCancelled = (response) => {
    console.log("Payment cancelled:", response);
    toastify("Payment cancelled successfully!", "success");
    // Update invoice data with updated status
    if (response.invoice) {
      setInvoiceData({ ...invoiceData, status: response.invoice.status });
    }
  };

  const getStatusBadge = (status) => {
    const statusClass =
      status === "Paid" || status === "Active"
        ? styles.statusPaid
        : status === "Pending"
          ? styles.statusPending
          : styles.statusOverdue;
    return (
      <span className={`${styles.statusBadge} ${statusClass}`}>
        {status || "Unknown"}
      </span>
    );
  };

  const getInvoiceType = () => {
    if (invoice?.licenseType) return "Licensed Billing";
    if (invoice?.unitCount !== undefined) return "Fixed Billing";
    if (invoice?.items) return "Facility Invoice";
    return "General Invoice";
  };

  if (!invoiceData) {
    return (
      <Layout>
        <div className="page-header">
          <div className="page-block">
            <div className="row align-items-center">
              <div className="col-md-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/core/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/core/billing">Billing Management</Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-body">
                <div className="alert alert-warning">
                  <i className="ti ti-alert-triangle me-2"></i>
                  No invoice data available. Please go back and select an
                  invoice.
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Get facilityId from invoice or facility
  const facilityId = invoiceData?.facilityId || facility?._id;

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/core/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/core/billing">Billing Management</Link>
                </li>
                <li className="breadcrumb-item active">
                  Invoice #{invoiceData.invoiceNumber || "N/A"}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            {/* Action Header */}
            <div className="card-header no-print">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <h5 className="mb-0">
                    Invoice #{invoiceData.invoiceNumber || "N/A"}
                  </h5>
                </div>
                <div className="col-md-6 text-end">
                  <div className="btn-group me-2" role="group">
                    <Button
                      label="Add Payment"
                      icon="ti ti-plus"
                      className="btn btn-success btn-sm"
                      onClick={() => setShowAddPaymentModal(true)}
                    />
                    <Button
                      label="View Payments"
                      icon="ti ti-list"
                      className="btn btn-info btn-sm"
                      onClick={() => setShowPaymentsListModal(true)}
                    />
                  </div>
                  <div className="btn-group" role="group">
                    <Button
                      label="Refresh Settings"
                      icon="ti ti-refresh"
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => {
                        setSettingsLoaded(false);
                        loadBillingSettings();
                      }}
                      loading={loadingSettings}
                    />
                    <Button
                      label="Print"
                      icon="ti ti-printer"
                      className="btn btn-success btn-sm"
                      onClick={handlePrint}
                      disabled={!displayHeaderSettings.companyName}
                    />
                    <PDFDownloadLink
                      document={
                        <InvoicePDF
                          invoice={invoiceData}
                          facility={facility}
                          displayHeaderSettings={displayHeaderSettings}
                          displayFooterSettings={displayFooterSettings}
                        />
                      }
                      fileName={getPDFFilename()}
                      className="btn btn-primary btn-sm text-decoration-none"
                    >
                      {({ blob, url, loading, error }) => (
                        <>
                          <i className="ti ti-download me-1"></i>
                          {loading ? "Generating..." : "Download PDF"}
                        </>
                      )}
                    </PDFDownloadLink>
                    <Button
                      label="Send Email"
                      icon="ti ti-mail"
                      className="btn btn-info btn-sm"
                      onClick={handleSendInvoice}
                      disabled={!displayHeaderSettings.companyName}
                    />
                    <Button
                      label="Back"
                      icon="ti ti-arrow-left"
                      className="btn btn-secondary btn-sm"
                      onClick={() => navigate(-1)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card-body">
              {/* Warning Message */}
              {!displayHeaderSettings.companyName && !loadingSettings && (
                <div className="alert alert-warning mb-3">
                  <i className="ti ti-alert-triangle me-2"></i>
                  <strong>Notice:</strong> Billing settings are not configured.
                  Please configure your company information in the Settings tab
                  to generate invoices.
                </div>
              )}

              {/* Invoice Display */}
              <div className={styles.invoiceContainer} id="printable-invoice">
                {/* Payment Status Badge */}
                {invoiceData.status && (
                  <div className="mb-3">
                    <span
                      className={`badge ${
                        invoiceData.status === "paid"
                          ? "bg-success"
                          : invoiceData.status === "pending"
                            ? "bg-warning"
                            : invoiceData.status === "overdue"
                              ? "bg-danger"
                              : "bg-secondary"
                      } fs-6`}
                    >
                      Status: {invoiceData.status.toUpperCase()}
                    </span>
                  </div>
                )}
                {/* Header */}
                <div className={styles.invoiceHeader}>
                  <div className={styles.headerContent}>
                    <div className={styles.rowAlignItemsStart}>
                      {/* Company Info */}
                      <div className={`${styles.colMd6} ${styles.colLg5}`}>
                        {displayHeaderSettings.logoPreview && (
                          <img
                            src={displayHeaderSettings.logoPreview}
                            alt="Company Logo"
                            className={`${styles.companyLogo} ${styles.mb3}`}
                          />
                        )}
                        <h1 className={styles.companyName}>
                          {displayHeaderSettings.companyName ||
                            "Your Company Name"}
                        </h1>
                        <div
                          className={`${styles.companyDetails} ${styles.mt2}`}
                        >
                          {displayHeaderSettings.companyAddress && (
                            <p className={styles.mb1}>
                              {displayHeaderSettings.companyAddress}
                            </p>
                          )}
                          {displayHeaderSettings.companyPhone && (
                            <p className={styles.mb1}>
                              <i className="ti ti-phone me-2"></i>
                              {displayHeaderSettings.companyPhone}
                            </p>
                          )}
                          {displayHeaderSettings.companyEmail && (
                            <p className={styles.mb1}>
                              <i className="ti ti-mail me-2"></i>
                              {displayHeaderSettings.companyEmail}
                            </p>
                          )}
                          {displayHeaderSettings.companyWebsite && (
                            <p className={styles.mb1}>
                              <i className="ti ti-world me-2"></i>
                              {displayHeaderSettings.companyWebsite}
                            </p>
                          )}
                          {displayHeaderSettings.taxNumber && (
                            <p className={styles.mb1}>
                              <i className="ti ti-file-text me-2"></i>
                              Tax No: {displayHeaderSettings.taxNumber}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Invoice Info */}
                      <div
                        className={`${styles.colMd6} ${styles.colLg7} ${styles.textEnd}`}
                      >
                        <h1 className={styles.invoiceTitle}>INVOICE</h1>
                        <div className={styles.invoiceMeta}>
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>
                              Invoice Number:
                            </span>
                            <span className={styles.metaValue}>
                              {invoice.invoiceNumber || "N/A"}
                            </span>
                          </div>
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>
                              Invoice Type:
                            </span>
                            <span className={styles.metaValue}>
                              {getInvoiceType()}
                            </span>
                          </div>
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>
                              Invoice Date:
                            </span>
                            <span className={styles.metaValue}>
                              {formatDate(invoice.createdAt || new Date())}
                            </span>
                          </div>
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Due Date:</span>
                            <span className={styles.metaValue}>
                              {formatDate(
                                invoice.dueDate ||
                                  invoice.validUntil ||
                                  invoice.nextDue,
                              )}
                            </span>
                          </div>
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Status:</span>
                            <span className={styles.metaValue}>
                              {getStatusBadge(invoice.status)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={styles.invoiceContent}>
                  {/* Billing Section */}
                  <div className={styles.billingSection}>
                    <div className={styles.row}>
                      {/* Bill To */}
                      <div className={styles.colMd6}>
                        <h3 className={styles.sectionTitle}>Bill To</h3>
                        <div className={styles.infoCard}>
                          <h4 className={styles.facilityName}>
                            {invoice.facilityName ||
                              facility?.name ||
                              "Facility Name"}
                          </h4>
                          <div className={styles.facilityDetails}>
                            {facility?.location && (
                              <p className={styles.mb1}>
                                <i className="ti ti-map-pin me-2"></i>
                                {facility.location}
                              </p>
                            )}
                            {facility?.address && (
                              <p className={styles.mb1}>
                                <i className="ti ti-building me-2"></i>
                                {facility.address}
                              </p>
                            )}
                            {facility?.contactPerson && (
                              <p className={styles.mb1}>
                                <i className="ti ti-user me-2"></i>
                                {facility.contactPerson}
                              </p>
                            )}
                            {facility?.email && (
                              <p className={styles.mb1}>
                                <i className="ti ti-mail me-2"></i>
                                {facility.email}
                              </p>
                            )}
                            {facility?.phone && (
                              <p className={styles.mb1}>
                                <i className="ti ti-phone me-2"></i>
                                {facility.phone}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Payment Terms */}
                      <div className={styles.colMd6}>
                        <h3 className={styles.sectionTitle}>Payment Terms</h3>
                        <div className={styles.infoCard}>
                          <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>
                              Payment Terms:
                            </span>
                            <span className={styles.infoValue}>
                              {displayFooterSettings.paymentTerms ||
                                "Net 30 days"}
                            </span>
                          </div>
                          {displayFooterSettings.latePaymentFee && (
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>
                                Late Payment Fee:
                              </span>
                              <span className={styles.infoValue}>
                                {displayFooterSettings.latePaymentFee}%
                              </span>
                            </div>
                          )}
                          {displayFooterSettings.taxRate && (
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>
                                Tax Rate:
                              </span>
                              <span className={styles.infoValue}>
                                {displayFooterSettings.taxRate}%
                              </span>
                            </div>
                          )}
                          <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>
                              Invoice Type:
                            </span>
                            <span className={styles.infoValue}>
                              {getInvoiceType()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Items Table */}
                  <div className={styles.invoiceItems}>
                    <table className={styles.itemsTable}>
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.items && Array.isArray(invoice.items) ? (
                          invoice.items.map((item, index) => (
                            <tr key={index}>
                              <td className={styles.itemDescription}>
                                <div className={styles.fwSemibold}>
                                  {item.description || item.name || "N/A"}
                                </div>
                                {item.detail && (
                                  <small className={styles.textMuted}>
                                    {item.detail}
                                  </small>
                                )}
                              </td>
                              <td className={styles.itemQuantity}>
                                {item.quantity || 1}
                              </td>
                              <td className={styles.itemPrice}>
                                {formatCurrency(
                                  item.unitPrice || item.amount || 0,
                                )}
                              </td>
                              <td className={styles.itemTotal}>
                                {formatCurrency(item.amount || 0)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td className={styles.itemDescription}>
                              <div className={styles.fwSemibold}>
                                {invoice.description ||
                                  invoice.licenseType ||
                                  "Service Charge"}
                              </div>
                              {invoice.unitCount && (
                                <small className={styles.textMuted}>
                                  @ KES{" "}
                                  {invoice.ratePerUnit?.toLocaleString() || 0}{" "}
                                  per unit
                                </small>
                              )}
                            </td>
                            <td className={styles.itemQuantity}>
                              {invoice.unitCount || 1}
                            </td>
                            <td className={styles.itemPrice}>
                              {formatCurrency(
                                invoice.ratePerUnit ||
                                  invoice.monthlyRate ||
                                  invoice.amount ||
                                  invoice.totalAmount ||
                                  0,
                              )}
                            </td>
                            <td className={styles.itemTotal}>
                              {formatCurrency(
                                invoice.amount || invoice.totalAmount || 0,
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className={styles.invoiceTotals}>
                    <table className={styles.totalsTable}>
                      <tbody>
                        <tr className={styles.subtotalRow}>
                          <td>Subtotal</td>
                          <td className={styles.amountValue}>
                            {formatCurrency(calculateSubtotal())}
                          </td>
                        </tr>
                        {(displayFooterSettings.taxRate || invoice?.taxRate) &&
                          calculateTax() > 0 && (
                            <tr className={styles.taxRow}>
                              <td>
                                Tax (
                                {displayFooterSettings.taxRate ||
                                  invoice?.taxRate}
                                %)
                              </td>
                              <td className={styles.amountValue}>
                                {formatCurrency(calculateTax())}
                              </td>
                            </tr>
                          )}
                        <tr className={styles.totalRow}>
                          <td>Total Amount</td>
                          <td className={styles.amountValue}>
                            {formatCurrency(calculateTotal())}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Footer */}
                <div className={styles.invoiceFooter}>
                  <div className={styles.row}>
                    {/* Payment Information */}
                    <div className={styles.colMd6}>
                      <h3 className={styles.sectionTitle}>
                        Payment Information
                      </h3>
                      <div className={styles.infoCard}>
                        {displayFooterSettings.bankName && (
                          <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>Bank Name:</span>
                            <span className={styles.infoValue}>
                              {displayFooterSettings.bankName}
                            </span>
                          </div>
                        )}
                        {displayFooterSettings.accountName && (
                          <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>
                              Account Name:
                            </span>
                            <span className={styles.infoValue}>
                              {displayFooterSettings.accountName}
                            </span>
                          </div>
                        )}
                        {displayFooterSettings.accountNumber && (
                          <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>
                              Account Number:
                            </span>
                            <span className={styles.infoValue}>
                              {displayFooterSettings.accountNumber}
                            </span>
                          </div>
                        )}
                        {displayFooterSettings.swiftCode && (
                          <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>
                              SWIFT Code:
                            </span>
                            <span className={styles.infoValue}>
                              {displayFooterSettings.swiftCode}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Support Information */}
                    <div className={styles.colMd6}>
                      <h3 className={styles.sectionTitle}>
                        Support Information
                      </h3>
                      <div className={styles.infoCard}>
                        {displayFooterSettings.supportEmail && (
                          <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>
                              Support Email:
                            </span>
                            <span className={styles.infoValue}>
                              {displayFooterSettings.supportEmail}
                            </span>
                          </div>
                        )}
                        {displayFooterSettings.supportPhone && (
                          <div className={styles.infoItem}>
                            <span className={styles.infoLabel}>
                              Support Phone:
                            </span>
                            <span className={styles.infoValue}>
                              {displayFooterSettings.supportPhone}
                            </span>
                          </div>
                        )}
                        {!displayFooterSettings.supportEmail &&
                          !displayFooterSettings.supportPhone && (
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Email:</span>
                              <span className={styles.infoValue}>
                                {displayHeaderSettings.companyEmail || "N/A"}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Notes */}
                  {displayFooterSettings.additionalNotes && (
                    <div className={`${styles.footerSection} ${styles.mt4}`}>
                      <h3 className={styles.sectionTitle}>Additional Notes</h3>
                      <div className={styles.notesSection}>
                        {displayFooterSettings.additionalNotes
                          .split("\n")
                          .map((line, index) => (
                            <p key={index} className={styles.mb1}>
                              {line || "N/A"}
                            </p>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Terms & Conditions */}
                  {displayFooterSettings.termsAndConditions && (
                    <div className={`${styles.footerSection} ${styles.mt4}`}>
                      <h3 className={styles.sectionTitle}>
                        Terms & Conditions
                      </h3>
                      <div className={styles.termsSection}>
                        {displayFooterSettings.termsAndConditions
                          .split("\n")
                          .map((line, index) => (
                            <p key={index} className={styles.mb1}>
                              {line || "N/A"}
                            </p>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Thank You Message */}
                  <div className={styles.thankYouMessage}>
                    <p className={styles.thankYouText}>
                      Thank you for your business! We value your partnership.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modals */}
      <AddPaymentModal
        visible={showAddPaymentModal}
        onHide={() => setShowAddPaymentModal(false)}
        invoice={invoiceData}
        facilityId={facilityId}
        onPaymentAdded={handlePaymentAdded}
      />

      <PaymentsListModal
        visible={showPaymentsListModal}
        onHide={() => setShowPaymentsListModal(false)}
        invoice={invoiceData}
        facilityId={facilityId}
        onPaymentCancelled={handlePaymentCancelled}
      />
    </Layout>
  );
}

export default InvoicePage;
